import { NSARouterMakerResult } from "namirasoft-account-react";
import { CurrencyExchangeInputRow, CurrencyExchangeRow, CurrencyRow, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { NSBox, NSBoxDateTime, NSBoxEntity, NSBoxString, NSColumn, NSLabel } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpEntities } from "../ErpEntities";
import { ErpLayout } from '../ErpLayout';
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScopeMode } from "../ErpScope";

interface Props extends NSARouterMakerResult, ErpRouterProps { isEdit: boolean; }

interface State { relationText: string; }

export default class CurrencyExchangeEditPage extends Component<Props, State>
{
    override state: State = { relationText: "" };

    DateTime = createRef<NSBoxDateTime>();
    FromCurrency = createRef<NSBoxEntity<CurrencyRow>>();
    ToCurrency = createRef<NSBoxEntity<CurrencyRow>>();
    RateNumerator = createRef<NSBoxString>();
    RateDenominator = createRef<NSBoxString>();

    updateRelation = () =>
    {
        try
        {
            const from = this.FromCurrency.current?.getDataOne(false) ?? null;
            const to = this.ToCurrency.current?.getDataOne(false) ?? null;
            const num = Number(this.RateNumerator.current?.getValue(false) ?? "");
            const den = Number(this.RateDenominator.current?.getValue(false) ?? "");
            if (!from || !to || !isFinite(num) || !isFinite(den) || den === 0)
            {
                if (this.state.relationText !== "")
                    this.setState({ relationText: "" });
                return;
            }
            const rate = (1 * num) / den;
            this.setState({ relationText: `Each 1 ${from.name} is converted to ${rate} ${to.name}` });
        } catch (error)
        {
            if (this.state.relationText !== "")
                this.setState({ relationText: "" });
        }
    };

    override render()
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);

        return (
            <ErpLayout<CurrencyExchangeRow, CurrencyExchangeInputRow>
                {...this.props}
                mode={ErpScopeMode.None}
                pages={{
                    entity: ErpEntities.currency_exchange(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): CurrencyExchangeInputRow => ({
                                        date_time: new Date(this.DateTime.current?.getValue() ?? new Date().toISOString()),
                                        from_currency_id: this.FromCurrency.current?.getValueOne() ?? "",
                                        to_currency_id: this.ToCurrency.current?.getValueOne() ?? "",
                                        rate_num: Number(this.RateNumerator.current?.getValue() ?? "0"),
                                        rate_den: Number(this.RateDenominator.current?.getValue() ?? "0"),
                                        currency_exchange_category: [],
                                        currency_exchange_tag: [],
                                        currency_exchange_field: [],
                                    }),
                                    setRow: (row: CurrencyExchangeRow) =>
                                    {
                                        this.DateTime.current?.setValue(new Date(row.date_time).toISOString());
                                        this.FromCurrency.current?.setValueOne(row.from_currency_id, this.updateRelation);
                                        this.ToCurrency.current?.setValueOne(row.to_currency_id, this.updateRelation);
                                        this.RateNumerator.current?.setValue(String(row.rate_num));
                                        this.RateDenominator.current?.setValue(String(row.rate_den));
                                        this.updateRelation();
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxDateTime ref={this.DateTime} required title="Date Time" info={{ link: "https://namirasoft.com/erp/console-guide/currency-exchange/#field-date-time" }} style={{ width: NSBox.width.double }} />
                    <NSBoxEntity<CurrencyRow>
                        ref={this.FromCurrency} title="From Currency" info={{ link: "https://namirasoft.com/erp/console-guide/currency-exchange/#field-from-currency" }} required multiple={false}
                        table_name="currency" table={db.tables.currency}
                        list={(filters, page, size, sorts) => this.props.servers.erp.currency.List(filters, page, size, sorts, null)}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        onChanged={this.updateRelation}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<CurrencyRow>
                        ref={this.ToCurrency} title="To Currency" info={{ link: "https://namirasoft.com/erp/console-guide/currency-exchange/#field-to-currency" }} required multiple={false}
                        table_name="currency" table={db.tables.currency}
                        list={(filters, page, size, sorts) => this.props.servers.erp.currency.List(filters, page, size, sorts, null)}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        onChanged={this.updateRelation}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxString ref={this.RateNumerator} required title="Rate Numerator" info={{ link: "https://namirasoft.com/erp/console-guide/currency-exchange/#field-rate-num" }} onChanged={this.updateRelation} style={{ width: NSBox.width.double }} />
                    <NSBoxString ref={this.RateDenominator} required title="Rate Denominator" info={{ link: "https://namirasoft.com/erp/console-guide/currency-exchange/#field-rate-den" }} onChanged={this.updateRelation} style={{ width: NSBox.width.double }} />
                    <NSLabel
                        title="Relation"
                        style={{ width: NSBox.width.double }}
                        description={this.state.relationText}
                    >
                    </NSLabel>
                </NSColumn>
            </ErpLayout>
        );
    }
}